import { AppTypeId } from '@ps/platform-message-framework/common/AppTypeId';
import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { ProductId } from '@ps/platform-message-framework/common/ProductId';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { RegulatorAccValEventType } from '@ps/platform-message-framework/regulator/RegulatorAccValEventType';
import { RegulatorBanPopupType } from '@ps/platform-message-framework/regulator/RegulatorBanPopupType';
import { RegulatorPlayerValPopupType } from '@ps/platform-message-framework/regulator/RegulatorPlayerValPopupType';
import {
  Cookie as CookieNames,
  ErrorCode,
  FeatureFlag,
  OperatorGuardPlayerStatus
} from '@ps/web-core-enums';
import { LoginSource } from '@ps/web-core-store';
import type { Modal, SessionResponse } from '@ps/web-core-store';
import { customEvent, getOrGenerateInstallId, isDefined } from '@ps/web-core-utils';
import arrayMove from 'array-move';
import jwtDecode from 'jwt-decode';
import { noop } from 'lodash';
import type { Subscription } from 'rxjs';
import { Subject } from 'rxjs';
import { filter, map, startWith, take, takeUntil, toArray, withLatestFrom } from 'rxjs/operators';
import Cookies from 'universal-cookie';

import { ConnectionType, connect } from '@swf/core/api/connect';
import { subscribe as apiSubscribe } from '@swf/core/api/subscribe';
import { CRMZone, SuspendAccountCode } from '@swf/core/enums';
import { StatusCode, StatusSubcode } from '@swf/core/enums/statusCode';
import type { RequestError } from '@swf/core/errors/RequestError';
import type { Dispatch, EffectParams, RootState } from '@swf/core/store';
import { getState, select } from '@swf/core/store';
import type {
  ForceLogoutNotification,
  LobbyNotification,
  NotificationMessage
} from '@swf/core/types/Notification';
import { NotificationCode } from '@swf/core/types/Notification';
import type { State } from '@swf/core/types/PSN';
import type { BoundRequestOptions, RequestReturn } from '@swf/core/types/Request';
import { timeout, velocityControl } from '@swf/core/utils';
import { tryFetch } from '@swf/core/utils/api';
import { setOddsFormatCookie } from '@swf/core/utils/setOddsFormatCookie';

import type { AuthError } from '.';
import type { LoginPayload } from './login';
import type { RegisterPayload } from './register';

interface ModalInfo {
  path: string;
  locationState?: object;
  shouldBeVisible: (
    rootState: RootState,
    license: LicenceId,
    loginSource: LoginSource | undefined,
    temporaryPassword: string | undefined
  ) => boolean;
}

const ONE_DAY_IN_MINUTES = 24 * 60;
const ONE_WEEK_IN_MINUTES = 24 * 60 * 7;
export const SECOND = 1000;
const subscriptions: Subscription[] = [];
const cookies = new Cookies();
let pending = false;

const delayedNotifications = [
  'notification.login.event',
  'notification.session.summary',
  'notification.lobby.msg',
  'notification.geocomply.locate',
  'notification.admin.info',
  'notification.player.blacklisted'
];

const logoutNotificationFilter = (message: NotificationMessage) =>
  ['session.logout.reply', 'notification.tokenExpireWarning'].includes(message.headers.type);

const forceLogoutNotificationFilter = (message: NotificationMessage) =>
  'notification.session.forcelogout' === message.headers.type;

const lobbyLogoutMessageNotificationFilter = (message: NotificationMessage) => {
  const {
    headers: { type },
    body: { notification }
  } = message;
  const { notificationCode } = notification as LobbyNotification;

  return type === 'notification.lobby.msg' && notificationCode === NotificationCode.Error;
};

const notificationComparator = (msgA: NotificationMessage, msgB: NotificationMessage) =>
  delayedNotifications.indexOf(msgA.headers.type) - delayedNotifications.indexOf(msgB.headers.type);

const initRewardsSubscription = (dispatch: Dispatch) => {
  const rootState = getState();
  const isExternalPartnerSite = select.cms.isExternalPartnerSite(rootState);

  if (select.cms.getSiteId(rootState) === Sites.IT_SISAL_EXT) {
    return;
  }

  if (select.feature.check(rootState, FeatureFlag.LoyaltyWidgetRefresh) && !isExternalPartnerSite) {
    dispatch.rewards.fetchRewards();
    return;
  }

  const connection = connect(
    isExternalPartnerSite ? ConnectionType.RewardsKong : ConnectionType.Rewards
  );

  let rewardsSubscriptionTopic = '/topic/widget/';
  if (isExternalPartnerSite) {
    const { idpToken } = select.session.get(getState());
    const jwtPayload: { sub: string } = jwtDecode(idpToken as string);
    rewardsSubscriptionTopic += btoa(jwtPayload?.sub);
  } else {
    const { token } = select.session.getLegacySession(rootState);
    rewardsSubscriptionTopic += token;
  }

  subscriptions.push(
    apiSubscribe(rewardsSubscriptionTopic, connection).subscribe(({ body }) =>
      dispatch.rewardsLegacy.enqueue(body)
    )
  );
};

export const subscribe = async (dispatch: Dispatch, loginObservable: Subject<boolean>) => {
  if (subscriptions.length) {
    return;
  }

  dispatch.notification.reset();
  const rootState = getState();
  const notificationPath = select.cms.getNotificationSubscriptionPath(rootState);
  let loginSuccess = false;

  initRewardsSubscription(dispatch);

  subscriptions.push(
    apiSubscribe<NotificationMessage>(notificationPath)
      .pipe(
        filter((message) =>
          forceLogoutNotificationFilter(message)
            ? false
            : loginSuccess || !delayedNotifications.includes(message.headers.type)
        )
      )
      .subscribe(dispatch.notification.push)
  );

  apiSubscribe<NotificationMessage>(notificationPath)
    .pipe(
      filter(({ headers: { type } }) => delayedNotifications.includes(type)),
      takeUntil(loginObservable),
      toArray(),
      map((notificationMessages) => {
        notificationMessages.sort(notificationComparator);
        return notificationMessages;
      })
    )
    .subscribe((delayedMessages) => {
      loginSuccess = true;
      delayedMessages.forEach((notification) => dispatch.notification.push(notification));
    });

  const lobbyNotification = apiSubscribe<NotificationMessage>(notificationPath).pipe(
    startWith(undefined),
    filter<NotificationMessage | undefined>(
      (message) => !message || lobbyLogoutMessageNotificationFilter(message)
    ),
    take(2)
  );

  subscriptions.push(
    apiSubscribe<NotificationMessage>('/user/queue/messages')
      .pipe<NotificationMessage, [NotificationMessage, NotificationMessage | undefined]>(
        filter(logoutNotificationFilter),
        withLatestFrom(lobbyNotification)
      )
      .subscribe(async ([logoutMessage, lobbyMessage]) => {
        await dispatch.notification.push(logoutMessage);
        if (lobbyMessage) {
          dispatch.notification.push(lobbyMessage);
        }
      })
  );

  subscriptions.push(
    apiSubscribe<NotificationMessage>(notificationPath)
      .pipe<NotificationMessage, [NotificationMessage, NotificationMessage | undefined]>(
        filter(forceLogoutNotificationFilter),
        withLatestFrom(lobbyNotification)
      )
      .subscribe(([logoutMessage, lobbyMessage]) => {
        const logoutNotification = logoutMessage.body.notification as ForceLogoutNotification;
        const { msg: logoutBodyMessage } = logoutNotification;

        if (!logoutBodyMessage && lobbyMessage) {
          const { msg: lobbyBodyMessage } = lobbyMessage.body.notification as LobbyNotification;
          logoutNotification.msg = lobbyBodyMessage;
          logoutMessage.body.notification = logoutNotification;
        }
        dispatch.notification.push(logoutMessage);
      })
  );
};

export const unsubscribe = () =>
  subscriptions
    .splice(0, subscriptions.length)
    .forEach((subscription) => subscription.unsubscribe());

const showBanModal = (rootState: RootState, loginSource: LoginSource | undefined) =>
  select.account.getBanPopup(rootState) !== RegulatorBanPopupType.NONE &&
  loginSource === LoginSource.Login &&
  select.account.isRealMoneyOK(rootState);

const loginModals: ModalInfo[] = [
  {
    path: '/notifications/cruks-blacklist',
    shouldBeVisible: (rootState, _, loginSource) =>
      select.cms.getSiteId(rootState) === Sites.NL &&
      loginSource === LoginSource.Login &&
      select.account.getPlayerValPopupType(rootState) === RegulatorPlayerValPopupType.BLACKLISTED
  },
  {
    path: '/notifications/bg-reinstated-account',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === LicenceId.BG &&
      loginSource === LoginSource.Login &&
      select.account.getBanPopup(rootState) === RegulatorBanPopupType.UN_BANNED
  },
  {
    path: '/notifications/cz-blacklist',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === LicenceId.CZ &&
      loginSource === LoginSource.Login &&
      [
        RegulatorPlayerValPopupType.BLACKLISTED,
        RegulatorPlayerValPopupType.DOC_VERIFICATION_REQUIRED,
        RegulatorPlayerValPopupType.RM_ACTIVITY_RESTRICTED
      ].includes(select.account.getPlayerValPopupType(rootState) as number)
  },
  {
    path: '/notifications/ee-population-register',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === LicenceId.ESTONIA &&
      loginSource === LoginSource.Login &&
      ([
        RegulatorPlayerValPopupType.DOC_VERIFICATION_REQUIRED,
        RegulatorPlayerValPopupType.RM_ACTIVITY_RESTRICTED
      ].includes(select.account.getPlayerValPopupType(rootState) as number) ||
        select.account.shouldPreventLogin(rootState))
  },
  {
    path: '/notifications/anj-national-exclusion',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === LicenceId.FRANCE &&
      loginSource === LoginSource.Login &&
      select.account.getBanPopup(rootState) === RegulatorBanPopupType.REMAIN_BANNED
  },
  {
    path: '/notifications/gamstop-exclusion',
    shouldBeVisible: (rootState) =>
      select.account.getWebClientDialog(rootState) === '/notifications/gamstop-exclusion'
  },
  {
    path: '/account/cashier/bankId',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === LicenceId.SE &&
      loginSource === LoginSource.Registration &&
      // TODO fix this
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (select.modal.getCurrent(rootState)?.locationState as any)?.bankId
  },
  {
    path: '/notifications/account-cancelled',
    shouldBeVisible: (rootState) => select.account.isAccountCancelled(rootState)
  },
  {
    path: '/auth/login/sms',
    shouldBeVisible: () =>
      parseInt(cookies.get(CookieNames.LIMITED_MODE), 10) === StatusCode.SMS_VALIDATION_ENABLED
  },
  {
    path: '/notifications/quick-account-creation',
    shouldBeVisible: (rootState) => select.account.isQuickAccountCreationRequired(rootState)
  },
  {
    path: '/account/migration',
    shouldBeVisible: (rootState) =>
      select.account.isMigrationRequired(rootState) &&
      !select.account.isQuickAccountCreationRequired(rootState)
  },
  {
    path: '/auth/login/eula',
    shouldBeVisible: (rootState) =>
      !select.account.isMigrationRequired(rootState) && select.account.isEulaRequired(rootState)
  },
  {
    path: '/notifications/reset-password',
    shouldBeVisible: (rootState, license, loginSource, temporaryPassword) => !!temporaryPassword
  },
  {
    path: '/account/cashier/rmf',
    shouldBeVisible: (rootState, _, loginSource) =>
      !select.account.isMigrationRequired(rootState) &&
      select.account.shouldOpenRmf(rootState, loginSource)
  },
  {
    path: '/notifications/forced-occupation',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === LicenceId.CAON &&
      loginSource === LoginSource.Login &&
      select.account.getWebClientDialog(rootState) === '/notifications/forced-occupation'
  },
  {
    path: '/notifications/occupation-attestation',
    shouldBeVisible: (rootState, _, loginSource) =>
      loginSource === LoginSource.Login &&
      (select.account.getWebClientDialog(rootState) === '/notifications/occupation-attestation' ||
        select.account.getWebClientDialog(rootState) === '/notifications/merged-occupation')
  },
  {
    path: '/security-investigation',
    shouldBeVisible: (rootState, license) =>
      select.account.isSuspendRM(rootState) &&
      (!select.cms.isSiteDE(rootState) ||
        (!select.account.isAgeVerified(rootState) &&
          !select.account.isTemporaryAccount(rootState))) &&
      !(
        license === LicenceId.CZ &&
        select.account.isKYCRequired(rootState) &&
        select.account.suspendRmReasonVerification(rootState)
      ) &&
      !(
        [LicenceId.BELGIUM, LicenceId.BG].includes(license) &&
        select.account.suspendRmReasonVerification(rootState)
      ) &&
      !select.account.isEEAccountValidationRequired(rootState)
  },
  {
    path: '/auth/force-change-password',
    shouldBeVisible: (rootState, license) =>
      license === LicenceId.SPAIN &&
      select.account.isRealMoneyOK(rootState) &&
      select.account.isPasswordWeak(rootState)
  },
  {
    path: '/notifications/over25-first-time',
    shouldBeVisible: (rootState, license) =>
      license === LicenceId.UK && select.account.showUkaYoungUserDialog(rootState)
  },
  {
    path: '/notifications/reinstate-account-initial',
    shouldBeVisible: (rootState, license) =>
      [
        LicenceId.EU,
        LicenceId.SPAIN,
        LicenceId.SE,
        LicenceId.DENMARK,
        LicenceId.ROMANIA,
        LicenceId.GR,
        LicenceId.BELGIUM,
        LicenceId.CZ,
        LicenceId.ESTONIA,
        LicenceId.BG
      ].includes(license) && select.account.showReinstateDialog(rootState)
  },
  {
    path: '/notifications/login-cash-out',
    shouldBeVisible: (rootState) => select.account.getForcedCashoutEnabled(rootState)
  },
  {
    path: '/notifications/documents-verification',
    shouldBeVisible: (rootState) => select.account.requireDocumentValidation(rootState)
  },
  {
    path: '/notifications/banned-by-regulator',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === LicenceId.SE && showBanModal(rootState, loginSource)
  },
  {
    path: '/notifications/oasis-national-exclusion',
    shouldBeVisible: (rootState, _, loginSource) =>
      select.cms.isSiteDE(rootState) && showBanModal(rootState, loginSource)
  },
  {
    path: '/notifications/prevent-login-vegas',
    shouldBeVisible: (rootState, _, loginSource) =>
      select.cms.isSiteDE(rootState) &&
      select.account.shouldPreventLogin(rootState) &&
      loginSource === LoginSource.Login &&
      select.account.isRealMoneyOK(rootState)
  },
  {
    path: '/notifications/lifetime-limit-acknowledgement',
    shouldBeVisible: (rootState, license) =>
      license === LicenceId.USNJ && select.account.checkLifetimeLimitAck(rootState)
  },
  {
    path: '/notifications/verify-withdrawal-method',
    shouldBeVisible: (rootState, _, loginSource) =>
      select.account.isVerifyWithdrawalMethod(rootState) && loginSource === LoginSource.Login
  },
  {
    path: '/notifications/poker-time-limit',
    shouldBeVisible: (rootState) =>
      select.account.getWebClientDialog(rootState) === '/notifications/poker-time-limit'
  },
  {
    path: '/notifications/login-information',
    shouldBeVisible: (_, license, loginSource) =>
      [
        LicenceId.SPAIN,
        LicenceId.SE,
        LicenceId.ITALY,
        LicenceId.DENMARK,
        LicenceId.PORTUGAL,
        LicenceId.BG,
        LicenceId.USNJ,
        LicenceId.USPA,
        LicenceId.GR,
        LicenceId.CAON
      ].includes(license) && loginSource === LoginSource.Login
  },
  {
    path: '/notifications/validate-account-reminder',
    shouldBeVisible: (rootState, license, loginSource) =>
      loginSource === LoginSource.Login &&
      ((license === LicenceId.GR && select.account.tempAccount(rootState)) ||
        (license === LicenceId.FRANCE &&
          [
            RegulatorAccValEventType.WAIT_DOCS,
            RegulatorAccValEventType.CLOSED_BY_DOCS,
            RegulatorAccValEventType.SUSPENDED_BY_DOCS,
            RegulatorAccValEventType.DOCS_MISMATCH,
            RegulatorAccValEventType.DOCS_MISMATCH_CORRECTED
          ].includes(select.account.getAccValEventType(rootState) as RegulatorAccValEventType)) ||
        select.account.isBeAccountValidationRequired(rootState) ||
        select.account.isBGAccountValidationRequired(rootState) ||
        select.account.isEEAccountValidationRequired(rootState))
  },
  {
    path: '/notifications/playing-limits-reminder',
    shouldBeVisible: (rootState, license, loginSource) =>
      loginSource === LoginSource.Login &&
      ((license === LicenceId.NL &&
        !(
          select.feature.check(rootState, FeatureFlag.LoginSEDialogue) &&
          select.account.selfExclusionExpired(rootState)
        )) ||
        license === LicenceId.CZ)
  },
  {
    path: '/notifications/login-limits',
    shouldBeVisible: (rootState, _, loginSource) =>
      loginSource === LoginSource.Login &&
      (!!select.account.getDepositConfirmLimits(rootState) ||
        !!select.account.getGameConfirmLimits(rootState))
  },
  {
    path: '/auth/docverify',
    shouldBeVisible: (rootState, license, loginSource) => {
      const currentModal = select.modal.getCurrent(rootState);

      return (
        // tmp solution to solve routing conflicts when dealing with embedded account routes
        currentModal?.path !== '/auth/docverify' &&
        currentModal?.path !== '/account/details/verify-account/' &&
        !select.account.getAccValEventType(rootState) &&
        ((select.feature.check(rootState, FeatureFlag.AMLLoginMsg) &&
          !!select.account.getAmlEventType(rootState) &&
          loginSource === LoginSource.Login &&
          select.cms.isSiteDE(rootState)) ||
          (select.account.isKYCRequired(rootState) && ![LicenceId.CZ].includes(license)) ||
          (select.account.isRealMoneyOK(rootState) &&
            !select.account.isAgeVerified(rootState) &&
            [LicenceId.FRANCE, LicenceId.ITALY].includes(license)))
      );
    }
  },
  {
    path: 'notifications/verify-account',
    shouldBeVisible: (rootState, _, loginSource) =>
      select.feature.check(rootState, FeatureFlag.AMLLoginMsg) &&
      !!select.account.getAmlEventType(rootState) &&
      loginSource === LoginSource.Login &&
      !select.cms.isSiteDE(rootState)
  },
  {
    path: '/notifications/login-account-information',
    shouldBeVisible: (rootState, license, loginSource) =>
      select.cms.isSiteDE(rootState) &&
      loginSource === LoginSource.Login &&
      (!!select.account.getAccountSummary(rootState) ||
        select.account.isDeAttestationSuspended(rootState))
  },
  {
    path: '/notifications/account-verified',
    shouldBeVisible: (rootState, _, loginSource) =>
      loginSource === LoginSource.Login &&
      select.feature.check(rootState, FeatureFlag.AMLLoginMsgVerified) &&
      select.account.amlVerified(rootState) &&
      select.account.amlShowVerifiedModal(rootState)
  },
  {
    path: '/notifications/pending-funds',
    shouldBeVisible: (rootState) =>
      !select.account.isAgeVerificationRequired(rootState) &&
      select.account.hasPendingFunds(rootState)
  },
  {
    path: '/notifications/casino-session-reminder',
    shouldBeVisible: (rootState) => select.account.isCasinoRealityCheckRequired(rootState)
  },
  {
    path: '/notifications/low-balance',
    shouldBeVisible: (rootState) =>
      select.feature.check(rootState, FeatureFlag.LowBalancePrompt) &&
      velocityControl(ONE_DAY_IN_MINUTES, select.localSettings.get(rootState).lowBalancePrompt)
  },
  {
    path: '/notifications/login-account-details',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === LicenceId.UK &&
      loginSource === LoginSource.Login &&
      select.account.accountDetailsRequired(rootState)
  },
  {
    path: '/notifications/self-excluded',
    shouldBeVisible: (rootState, license, loginSource) =>
      [LicenceId.USNJ, LicenceId.USPA, LicenceId.USMI].includes(license) &&
      loginSource === LoginSource.Login &&
      select.account.getInfoSelfExcludedMessage(rootState)
  },
  {
    path: '/notifications/value-proposition/before-you-begin',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === LicenceId.UK &&
      loginSource === LoginSource.Login &&
      select.account.displayValueProposition(rootState)
  },
  {
    path: '/notifications/communications-preferences',
    shouldBeVisible: (rootState, license) =>
      license === LicenceId.UK && select.account.showCommunicationPreferencesPopup(rootState)
  },
  {
    path: '/notifications/active-self-exclusion',
    shouldBeVisible: (rootState, _, loginSource) =>
      select.feature.check(rootState, FeatureFlag.LoginSEDialogue) &&
      loginSource === LoginSource.Login &&
      select.account.isSelfExcluded(rootState)
  },
  {
    path: '/notifications/expired-self-exclusion',
    shouldBeVisible: (rootState) =>
      select.feature.check(rootState, FeatureFlag.LoginSEDialogue) &&
      select.account.selfExclusionExpired(rootState)
  },
  {
    path: '/notifications/playing-insights',
    shouldBeVisible: (rootState, _, loginSource) =>
      loginSource === LoginSource.Login &&
      select.account.isRealMoneyOK(rootState) &&
      select.feature.check(rootState, FeatureFlag.VisibilityPlayingInsightsLoginPopup) &&
      velocityControl(ONE_WEEK_IN_MINUTES, select.localSettings.get(rootState).playingInsights)
  },
  {
    path: '/notifications/responsible-gaming-warning',
    shouldBeVisible: (_, license, loginSource) =>
      [LicenceId.ESTONIA, LicenceId.BELGIUM].includes(license) && loginSource === LoginSource.Login
  },
  {
    path: '/notifications/id-expiring',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === LicenceId.DE &&
      loginSource === LoginSource.Login &&
      select.account.getAccValEventType(rootState) === RegulatorAccValEventType.WAIT_DOCS
  },
  {
    path: '/notifications/id-expired',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === LicenceId.DE &&
      loginSource === LoginSource.Login &&
      select.account.getAccValEventType(rootState) === RegulatorAccValEventType.SUSPENDED_BY_DOCS
  },
  {
    path: '/notifications/de-annual-attestation',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === LicenceId.DE &&
      loginSource === LoginSource.Login &&
      select.account.isDeAnnualAttestation(rootState)
  }
];

export const getModalsByLicense = (modals: ModalInfo[], license: LicenceId) => {
  if ([LicenceId.SPAIN, LicenceId.ITALY, LicenceId.SE].includes(license)) {
    const fromIndex = modals.findIndex((el) => el.path === '/account/cashier/rmf');
    const toIndex = modals.findIndex((el) => el.path === '/notifications/login-information');

    return arrayMove(modals, fromIndex, toIndex);
  }

  return modals;
};

export const isHardVerificationLicense = (license: LicenceId) =>
  [LicenceId.DE, LicenceId.ROMANIA, LicenceId.SPAIN, LicenceId.GR, LicenceId.FRANCE].includes(
    license
  );

export const verifyOneTrustCookie = async (): Promise<void> => {
  const webId = cookies.get(CookieNames.WEB_ID);
  const { OneTrust } = window;
  const consentIntegrationData = OneTrust?.GetDomainData().ConsentIntegrationData;
  const CCID = consentIntegrationData?.consentPayload.identifier;
  if (!consentIntegrationData || (webId && webId === CCID)) {
    return;
  }

  OneTrust?.setDataSubjectId(webId);
  consentIntegrationData.consentPayload.isAnonymous = false;
  consentIntegrationData.consentPayload.identifier = webId;

  try {
    tryFetch(consentIntegrationData.consentApi, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(consentIntegrationData.consentPayload)
    });
  } catch {
    // do noting
  }
};

export const fetchAccountDetails = async (
  { token, loginSource }: SessionResponse,
  { dispatch, rootState }: Pick<EffectParams, 'dispatch' | 'rootState'>
) => {
  const oneTrustEnabled = select.feature.check(rootState, FeatureFlag.EnableOneTrust);

  token && dispatch.session.update({ token });

  await Promise.all([
    dispatch.account
      .fetch()
      .then(() => setOddsFormatCookie(select.account.getOddsFormat(getState()))),
    dispatch.feature.check().catch(noop)
  ]);

  if (oneTrustEnabled && [LoginSource.Login, LoginSource.Registration].includes(loginSource)) {
    verifyOneTrustCookie();
  }

  dispatch.session.response({ loginSource });
};

export const sessionTransfer = async (
  options: BoundRequestOptions,
  { request }: Pick<EffectParams, 'request'>
): RequestReturn => {
  try {
    return await request(options);
  } catch (error) {
    const { body } = error as RequestError;
    if (body.code === ErrorCode.TRANSFER_APC_NOT_READY) {
      await timeout(1000);
      return sessionTransfer(options, { request });
    }

    throw error;
  }
};

export async function checkLugasStatus(dispatch: Dispatch) {
  try {
    const { status } = await dispatch.account.getLugasStatus();
    if (status === OperatorGuardPlayerStatus.PENDING_LONG_SESSION) {
      dispatch.modal.unshift({ path: '/notifications/playing-timeout-break' });
    }
  } catch {
    // do nothing
  }
}

export const loginSuccess = async (
  session: SessionResponse,
  { dispatch }: Pick<EffectParams, 'dispatch'>,
  temporaryPassword?: string
) => {
  if (pending) return;

  let rootState = getState();
  const { loginSource } = session;
  const loginObservable = new Subject<boolean>();
  const isExternalPartnerSite = select.cms.isExternalPartnerSite(rootState);

  try {
    const location = select.modal.getCurrent(rootState);
    pending = true;
    await fetchAccountDetails(session, { dispatch, rootState });

    try {
      subscribe(dispatch, loginObservable);
      if (!isExternalPartnerSite) await dispatch.casino.fetchAppData();
    } catch {
      subscribe(dispatch, loginObservable);
    }

    rootState = getState();
    const license = select.account.getLicense(rootState);

    const modals = getModalsByLicense(loginModals, license)
      .filter((modal) => modal.shouldBeVisible(rootState, license, loginSource, temporaryPassword))
      .map(({ path, locationState }) => ({
        path,
        locationState: { ...locationState, temporaryPassword }
      }));

    if (
      modals.length &&
      !select.device.isEmbedded(rootState) &&
      (!select.location.isEmbeddedRoute(rootState) || loginSource !== LoginSource.VerifySession)
    ) {
      if (!select.location.isEmbeddedRoute(rootState)) {
        dispatch.modal.close('/auth');
        dispatch.modal.enqueue(modals);
      } else {
        dispatch.modal.replace(modals);
      }
    } else if ([LoginSource.Login, LoginSource.Registration].includes(session.loginSource)) {
      dispatch.modal.close('/auth');
    }

    const { restrictPlayingTime } = select.localSettings.get(rootState);
    if (restrictPlayingTime) {
      dispatch.modal.enqueue({
        path: '/notifications/restrict-playing-time',
        locationState: restrictPlayingTime
      });
    }

    const { sessionSummary } = select.localSettings.get(rootState);
    if (sessionSummary) {
      dispatch.modal.unique({
        path: '/notifications/session-summary-notification',
        locationState: sessionSummary
      });
    }

    if (!cookies.get(CookieNames.MY_NEWS_SEEN) && loginSource === LoginSource.Login) {
      await dispatch.crm.fetchZone({ zoneId: CRMZone.MY_NEWS });
      const contents = select.crm.getZone(getState(), CRMZone.MY_NEWS);
      if (contents?.length) {
        cookies.set(CookieNames.MY_NEWS_SEEN, true);
        dispatch.modal.enqueue({
          path: `/stars-crm/${CRMZone.MY_NEWS}`,
          locationState: { componentType: 'NavCarousel' }
        });
      }
    }

    if (loginSource === LoginSource.Login && (location?.locationState as { path: string })?.path) {
      dispatch.modal.enqueue(location?.locationState as Modal | Modal[]);
    }

    if (license === LicenceId.DE) checkLugasStatus(dispatch);
  } finally {
    pending = false;
    loginObservable.next(true);
    loginObservable.complete();
  }
};

const encryptContext = async (context: LoginPayload) => {
  const enc = new TextEncoder();

  const key = await window.crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256'
    },
    true,
    ['encrypt', 'decrypt']
  );

  const privateKey = JSON.stringify(await window.crypto.subtle.exportKey('jwk', key.privateKey));
  const flowId = window.crypto.randomUUID();

  const ciphertext = await window.crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    key.publicKey,
    enc.encode(JSON.stringify(context))
  );

  const buffer = new Uint8Array(ciphertext);

  localStorage.setItem('mitId', JSON.stringify({ flowId, privateKey }));

  return { flowId, mitIdAuth: true, buffer };
};

export const loginError = async (
  payload: LoginPayload | RegisterPayload,
  error: RequestError<AuthError>,
  dispatch: Dispatch
) => {
  const rootState = getState();
  const { statusSubcode } = error.headers || {};
  const code = error.body?.code;
  const site = select.cms.getSiteId(rootState);
  const isSiteUS = select.cms.isSiteUS(rootState);
  const isSiteDE = select.cms.isSiteDE(rootState);
  const isSiteBG = site === Sites.BUL;
  const isSiteDK = site === Sites.DEN;
  const isSiteGR = site === Sites.GR;

  // TODO remove with RAM1 integration
  customEvent({ tag: 'loginError', data: error });

  switch (code) {
    case isSiteBG && ErrorCode.BLOCKED_BLACKLISTED:
      dispatch.modal.replace([{ path: '/notifications/bg-blocked-blacklisted-account' }]);
      break;
    case isSiteDK &&
      statusSubcode === StatusSubcode.AUTH_SUB_AUTH_ERROR_ACCOUNT_CLOSED &&
      ErrorCode.LOGIN_MSG_NOT_ALLOWED:
    case isSiteDK && ErrorCode.ACCOUNT_CLOSED_AND_BBR:
      dispatch.modal.replace([{ path: '/notifications/perm-closed-and-bbr' }]);
      break;
    case isSiteDK && ErrorCode.PERMANENTLY_EXCLUDED:
      dispatch.modal.replace([{ path: '/notifications/perm-excluded' }]);
      break;
    case ErrorCode.TIME_LIMIT_REACHED:
      dispatch.modal.replace([
        {
          path: '/notifications/login-restrictions',
          locationState: { ...error.body }
        }
      ]);
      break;
    case ErrorCode.TOTAL_LIMIT_REACHED:
      dispatch.modal.replace([
        {
          path: '/notifications/login-total-restrictions',
          locationState: { ...error.body }
        }
      ]);
      break;
    case ErrorCode.RSA_REQUIRED:
      dispatch.modal.replace([{ path: '/auth/login/rsa', locationState: payload }]);
      break;
    case ErrorCode.PIN_REQUIRED:
    case ErrorCode.TEMP_PIN_REQUIRED:
    case ErrorCode.TEMP_PIN_REQUIRED_SMS:
      dispatch.modal.replace([
        {
          path: '/auth/login/pin',
          locationState: {
            path: (select.modal.getCurrent(rootState)?.locationState as { path: string })?.path,
            ...payload,
            errorCode: code
          }
        }
      ]);
      break;
    case ErrorCode.NEMID_LOGIN_REQUIRED:
      dispatch.modal.replace([
        { path: '/auth/login/nemid', locationState: { ...payload, ...error.body } }
      ]);
      break;
    case ErrorCode.ACCOUNT_CLOSED:
      if (site === Sites.DEN) {
        dispatch.modal.replace([{ path: '/notifications/account-closed' }]);
      }
      if (site === Sites.POR) {
        dispatch.modal.replace([
          {
            path: '/notifications/suspended-account',
            locationState: { code: SuspendAccountCode.Blacklisted }
          }
        ]);
      }
      if (site === Sites.SWE) {
        dispatch.modal.replace([{ path: '/notifications/swe-indefinite-exclusion' }]);
      }

      dispatch.auth.error(error);
      break;
    case isSiteUS && ErrorCode.BANNED_BY_REGULATOR:
      dispatch.modal.replace([{ path: '/notifications/banned-by-regulator' }]);
      break;
    case [Sites.UK, Sites.CA_ON].includes(site) && ErrorCode.SELF_EXCLUDED: {
      const selfExclusionEndDate = error.body.selfExclusionEndTime;
      if (new Date(Number(selfExclusionEndDate)) < new Date()) {
        dispatch.modal.replace([{ path: '/notifications/expired-self-excluded-popup' }]);
      } else {
        dispatch.modal.replace([
          {
            path: '/notifications/self-excluded-popup',
            locationState: { selfExclusionEndTime: selfExclusionEndDate }
          }
        ]);
      }
      break;
    }
    case isSiteGR && ErrorCode.SESSION_COOL_OFF:
      dispatch.modal.replace([
        {
          path: '/notifications/session-cool-off',
          locationState: { longSessionCoolOff: error.body.longSessionCoolOff }
        }
      ]);
      break;
    case ErrorCode.LOGIN_COOL_OFF:
      dispatch.modal.replace([
        {
          path: '/notifications/login-cool-off',
          locationState: { loginCoolOffLimit: error.body.loginCoolOffLimit }
        }
      ]);
      break;
    case ErrorCode.PANIC_BUTTON_EXCLUDED:
      if (site === Sites.SWE) {
        dispatch.modal.replace([
          {
            path: '/notifications/panic-button',
            locationState: { panicButtonExclusionEndTime: error.body.panicButtonExclusionEndTime }
          }
        ]);
      }
      if (isSiteDE) {
        dispatch.modal.replace([{ path: '/notifications/cooling-off-period' }]);
      }
      break;
    case ErrorCode.APC_ON_DIFF_DEVICE:
      if ((payload as LoginPayload).rsa) {
        dispatch.auth.error(error);
        break;
      }

      dispatch.modal.replace([{ path: '/auth/session-transfer', locationState: payload }]);
      break;
    case ErrorCode.MITID_AUTH_REQUIRED:
      // eslint-disable-next-line no-case-declarations
      const contextData = await encryptContext(payload);
      dispatch.modal.replace([
        { path: '/account/mitId', locationState: { path: '/auth/login/', ...contextData } }
      ]);
      break;
    case isSiteDE && ErrorCode.PREVENT_LOGIN:
      dispatch.modal.replace([{ path: '/notifications/prevent-login-vegas' }]);
      break;
    case ErrorCode.LOGIN_MSG_STRONG_AUTH_REQUIRED:
      dispatch.modal.replace([{ path: '/auth/login/security-questions', locationState: payload }]);
      break;
    default:
      dispatch.auth.error(error);
  }

  return code;
};

export const generatePSNState = (code?: string): string => {
  const rootState = getState();
  const installId = select.session.getInstallId(rootState) ?? getOrGenerateInstallId();
  const siteId = select.cms.getSiteId(rootState);
  const deviceId = select.device.getId(rootState);
  const osId = select.device.getOS(rootState);
  const brandId = select.cms.getBrandId(rootState);
  const platformId = select.device.getPlatform(rootState);
  const psnAuthCallback = isDefined(code);

  const state: State = {
    installId,
    siteId,
    deviceId,
    osId,
    brandId,
    platformId,
    productId: ProductId.POKER,
    appTypeId: AppTypeId.RAM,
    macAddress: null,
    routerMacAddress: null,
    volumeLabel: null,
    psnAuthCallback
  };

  return btoa(JSON.stringify(state));
};
